import { ServerError } from '@src/common-utils/AuthModels';

/** Prefix of the 409 message the API returns when an order references a deleted product. */
export const PRODUCT_UNAVAILABLE_PREFIX = 'Product(s) no longer available';

/**
 * Splits stored cart lines into those whose product still exists and those that no longer do (an admin may have
 * deleted the product while it sat in the cart).
 *
 * `products` is what the API returned for the stored codes. `undefined` means the lookup itself FAILED (network,
 * server error): in that case nothing may be treated as unavailable, or a flaky connection would empty the cart.
 */
export const splitCartByAvailability = <T extends { productCode: string }>(
  storedItems: T[],
  products: { code: string }[] | undefined,
): { available: T[]; unavailable: T[] } => {
  if (!products) {
    return { available: storedItems, unavailable: [] };
  }

  const existingCodes = new Set(products.map((product) => product.code));
  return {
    available: storedItems.filter((item) => existingCodes.has(item.productCode)),
    unavailable: storedItems.filter((item) => !existingCodes.has(item.productCode)),
  };
};

export const unavailableItemsMessage = (codes: string[]): string => {
  const unique = [...new Set(codes)];
  return `${
    unique.length === 1 ? '1 item was' : `${unique.length} items were`
  } no longer available and removed from your cart (${unique.join(', ')}).`;
};

/** True when an order was refused because a product in it no longer exists. */
export const isProductUnavailableError = (error: unknown): boolean =>
  error instanceof ServerError && error.statusCode === 409 && error.message.startsWith(PRODUCT_UNAVAILABLE_PREFIX);
